import Api from '@sancare/typescript-commons/src/misc/api'

import ApiLegacy from '@/misc/api'

// If we are logged in but need to resume a session,
// we delay all subsequent API calls
if (localStorage.getItem('username')) {
  ApiLegacy.startDelayingCalls()
  Api.startDelayingCalls()
}

export default {
  state: {
    username: localStorage.getItem('username'),
    userRole: localStorage.getItem('userRole'),
    currentUser: {
      id: null,
      role: null,
      editableRoles: [],
      teamMode: null,
      sancareUser: false,
      createdFromOidc: Boolean(localStorage.getItem('createdFromOidc')),
      team: {
        id: null,
      },
    },
    // If the session was unvalidated and never restarted last time, we are brought back to the login
    isAuthenticated: Boolean(localStorage.getItem('username') && localStorage.getItem('sessionCanBeResumed')),
    getSelfRequest: { fetching: false, error: null, ok: false },
    updateSelfRequest: { fetching: false, error: null, ok: false },
    authRequest: { fetching: false, error: null, ok: false },
    expiredPassword: false,
    expiredPasswordRequest: { fetching: false, error: null, ok: false },
    expiredData: { username: null },
    logoutRequest: { fetching: false, error: null, ok: false },
  },
  getters: {
    userRole: (state) => state.userRole,
    isAuthenticated: (state) => state.isAuthenticated,
    username: (state) => state.username,
  },
  mutations: {
    getSelfStart(state) {
      state.getSelfRequest = { fetching: true, error: null, ok: false }
    },
    getSelfSuccess(state, user) {
      state.getSelfRequest.fetching = false
      state.getSelfRequest.ok = true
      state.currentUser = user

      // Persisted so the session-expired modal still knows the account is OIDC-based once getSelf 401s.
      if (user.createdFromOidc) {
        localStorage.setItem('createdFromOidc', 1)
      } else {
        localStorage.removeItem('createdFromOidc')
      }

      // A successful call proves the session cookie is valid, which covers OIDC logins that never go through authRequestSuccess.
      if (!state.isAuthenticated) {
        state.username = user.username
        localStorage.setItem('username', user.username)
        state.userRole = user.role
        localStorage.setItem('userRole', user.role)
        state.isAuthenticated = true
        localStorage.setItem('sessionCanBeResumed', 1)
      }

      ApiLegacy.resumeDelayedCalls()
      Api.resumeDelayedCalls()
    },
    getSelfError(state, res) {
      state.getSelfRequest.fetching = false
      state.getSelfRequest.error = res.message
    },
    updateSelfStart(state) {
      state.updateSelfRequest = { fetching: true, error: null, ok: false }
    },
    updateSelfSuccess(state, user) {
      state.updateSelfRequest = { fetching: false, error: null, ok: true }
      state.currentUser = user
    },
    updateSelfError(state, res) {
      state.updateSelfRequest = { fetching: false, error: res.message, ok: false }
    },
    resetUpdateSelf(state) {
      state.updateSelfRequest = { fetching: false, error: null, ok: false }
    },
    authRequestStart(state) {
      state.authRequest = { fetching: true, error: null, ok: false }
      state.expiredPassword = false
    },
    authRequestSuccess(state, response) {
      state.authRequest.fetching = false
      state.authRequest.error = null
      state.authRequest.ok = true

      state.username = response.username
      localStorage.setItem('username', response.username)
      state.userRole = response.role
      localStorage.setItem('userRole', response.role)
      state.currentUser.id = response.id

      state.isAuthenticated = true
      localStorage.setItem('sessionCanBeResumed', 1)
      ApiLegacy.resumeDelayedCalls()
      Api.resumeDelayedCalls()
    },
    authRequestError(state, res) {
      state.authRequest.fetching = false
      state.authRequest.error = res.message
      state.expiredPassword = Boolean(res.rawError.expiredPassword && res.rawError.expiredPassword === 1)
    },
    authRequestReset(state) {
      state.authRequest = { fetching: false, error: null, ok: false }
    },
    expiredPasswordRequestStart(state) {
      state.expiredPasswordRequest = { fetching: true, error: null, ok: false }
    },
    expiredPasswordRequestSuccess(state) {
      state.expiredPasswordRequest = { fetching: false, error: null, ok: true }
      state.expiredPassword = false
    },
    expiredPasswordRequestError(state, res) {
      state.expiredPasswordRequest = { fetching: false, error: res.message, ok: false }
    },
    expiredPasswordRequestReset(state) {
      state.expiredPasswordRequest = { fetching: false, error: null, ok: false }
      state.expiredPassword = false
    },
    expiredData(state, expiredData) {
      state.expiredData = expiredData
    },
    logoutRequestStart(state) {
      state.logoutRequest = { fetching: true, error: null, ok: false }
    },
    logoutRequestSuccess(state) {
      state.logoutRequest.fetching = false
      state.logoutRequest.ok = true

      state.username = null
      state.userRole = null
      state.isAuthenticated = false
      localStorage.clear()
      sessionStorage.clear()
      ApiLegacy.rejectDelayedCalls()
      Api.rejectDelayedCalls()
    },
    logoutRequestError(state, res) {
      state.logoutRequest.fetching = false
      state.logoutRequest.error = res.message
    },
    sessionExpired(state) {
      state.sessionIsValid = false
      localStorage.removeItem('sessionCanBeResumed')
    },
  },
  actions: {
    authenticate({ commit }, { username, password }) {
      return ApiLegacy.post(
        {
          url: '/api/authenticate',
          body: { username, password },
          startMut: 'authRequestStart',
          successMut: 'authRequestSuccess',
          errorMut: 'authRequestError',
          doNotRetry: true,
          cannotBeDelayed: true,
        },
        commit
      )
    },
    expiredPassword({ commit }, { username, password, newPassword, confirmationPassword }) {
      return ApiLegacy.post(
        {
          url: '/api/expired-password',
          body: { username, password, newPassword, confirmationPassword },
          startMut: 'expiredPasswordRequestStart',
          successMut: 'expiredPasswordRequestSuccess',
          errorMut: 'expiredPasswordRequestError',
          doNotRetry: true,
          cannotBeDelayed: true,
        },
        commit
      )
    },
    logout({ commit }) {
      return ApiLegacy.get(
        {
          url: '/api/logout',
          startMut: 'logoutRequestStart',
          successMut: 'logoutRequestSuccess',
          errorMut: 'logoutRequestError',
          doNotRetry: true,
          cannotBeDelayed: true,
        },
        commit
      )
    },
  },
}
